import React, { useState, ComponentProps } from 'react';
import classnames from 'classnames';
import { RecommendCard } from 'components/RecommendCard';
import { Button, ArrowDown } from '@doda/io-react/common';
import { Result as RecommendJob } from '@doda/common/src/interfaces/api/recommendPanel';
import { JobDetailTop4AsHighlyRecommendAbTestGroup } from '@doda/common/src/types/markTop4AsHighlyRecommend';
import styles from './index.module.scss';
import { JobListForRecommend } from '../../pages/DodaFront/View/JobHistoryList/logics/generateJobListForRecommend/generateJobListForRecommend';

const DEFAULT_DISPLAY_CARD_NUMBER = 12;

type RecommendProps = ComponentProps<typeof RecommendCard>;

/**
 * RecommendGridコンポーネントのProps
 */
interface Props {
  /** おすすめの求人リスト */
  recommendList: RecommendJob[] | JobListForRecommend[];
  /** お気に入りボタンがクリックされた時のハンドラー（任意） */
  onClickFavorite?: (
    recommend: RecommendJob | JobListForRecommend,
    index: number,
    jid: string
  ) => void;
  /** 保存ボタンがクリックされた時のハンドラー */
  onClickSaved: RecommendProps['onClickSaved'];
  /** サムネイルがクリックされた時のハンドラー（任意） */
  onClickThumbnail?: (
    recommendJob: RecommendJob | JobListForRecommend,
    index: number,
    jid?: string
  ) => void;
  /** カード詳細がクリックされた時のハンドラー（任意） */
  onClickCardDetail?: (
    recommendJob: RecommendJob | JobListForRecommend,
    index: number,
    jid?: string
  ) => void;
  /** タイトル */
  title: string;
  /** ブックマークリストのURL */
  bookmarkListUrl: RecommendProps['bookmarkListUrl'];
  /** ブックマーク保存リクエストボディ */
  saveBookmarkRequestBody: RecommendProps['saveBookmarkRequestBody'];
  /** 追加のクエリ文字列（任意） */
  additionalQueryString?: string;
  /** 影の色（デフォルトはblue） */
  shadowColor?: 'blue' | 'black';
  /** おすすめタイプ */
  recommendType: string;
  isWideCard?: boolean;
  /** AB テストパターン（TEST1 は yellow、TEST2 は blue border） */
  jobDetailHighlyRecommendPattern?: JobDetailTop4AsHighlyRecommendAbTestGroup;
  description?: string;
}

/**
 * RecommendGridコンポーネント
 * おすすめの求人をグリッド表示するコンポーネント
 * @param {Props} props - コンポーネントのプロパティ
 * @returns {JSX.Element} RecommendGridのJSX要素
 */
export const RecommendGrid: React.FC<Props> = ({
  recommendList,
  onClickFavorite,
  onClickSaved,
  onClickThumbnail,
  onClickCardDetail,
  title,
  bookmarkListUrl,
  saveBookmarkRequestBody,
  additionalQueryString,
  shadowColor = 'blue',
  recommendType,
  isWideCard = false,
  jobDetailHighlyRecommendPattern,
  description = '',
}) => {
  const [isMoreButton, setIsMoreButton] = useState(
    recommendList.length > DEFAULT_DISPLAY_CARD_NUMBER
  );

  const displayRecommendJobList = isMoreButton
    ? recommendList.slice(0, DEFAULT_DISPLAY_CARD_NUMBER)
    : recommendList;

  return (
    <>
      <input
        name="recommendIdx"
        id="recommendIdx"
        className={`recommendIdx${recommendType}`}
        type="hidden"
        value="1"
      />
      <h3
        className={classnames(styles.recommendGrid__title, {
          [styles['recommendGrid__title--withDescription']]: description,
        })}
      >
        {title}
      </h3>
      {description && (
        <p className={styles.recommendGrid__description}>{description}</p>
      )}
      <div className={styles.recommendGrid__grid}>
        {displayRecommendJobList.map((recommend, index) => {
          return (
            <RecommendCard
              key={index}
              imageUrl={recommend.imgUrl}
              altText={recommend.companyName}
              companyName={recommend.companyName}
              jobName={recommend.jobName}
              area={recommend.area}
              income={recommend.income}
              jobDetailUrl={recommend.url}
              isNew={recommend.isNew}
              recommendType={recommend.recommendType}
              additionalQueryString={additionalQueryString}
              isDeadline={recommend.isDeadline}
              onClickFavorite={() => {
                if (onClickFavorite)
                  onClickFavorite(recommend, index, recommend.itemNo);
              }}
              onClickSaved={onClickSaved}
              onClickThumbnail={() => {
                if (onClickThumbnail)
                  onClickThumbnail(recommend, index, recommend.itemNo);
              }}
              onClickCardDetail={() => {
                if (onClickCardDetail)
                  onClickCardDetail(recommend, index, recommend.itemNo);
              }}
              bookmarkListUrl={bookmarkListUrl}
              jid={recommend.itemNo}
              saveBookmarkRequestBody={saveBookmarkRequestBody}
              shadowColor={shadowColor}
              isBookmarked={recommend.saveBookmark}
              isWide={isWideCard}
              jobDetailHighlyRecommendPattern={jobDetailHighlyRecommendPattern}
              isHighlyRecommended={
                (recommend as RecommendJob).isHighlyRecommended
              }
            />
          );
        })}
      </div>
      {isMoreButton && (
        <div className={styles.recommendGrid__buttonWrapper}>
          <Button
            size="L"
            widthPercentage={100}
            className={styles.recommendGrid__button}
            variant="outline_blue"
            onClick={() => setIsMoreButton(false)}
          >
            もっと見る
            <ArrowDown color="blue" size="M" />
          </Button>
        </div>
      )}
    </>
  );
};
